import { useState, useEffect } from 'react'

export interface BusinessCost {
  id: string
  businessId: string
  name: string
  category: string
  amount: number
  frequency: string
  costBehavior: string
  notes?: string | null
  createdAt: string
  updatedAt: string
}

export interface CostSummary {
  totalFixed: number
  totalVariable: number
  totalMonthly: number
  totalByCategory: Record<string, number>
}

export function useCosts(businessId?: string) {
  const [costs, setCosts] = useState<BusinessCost[]>([])
  const [summary, setSummary] = useState<CostSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCosts = async () => {
    if (!businessId) return
    setIsLoading(true)
    try {
      const res = await fetch(`/api/businesses/${businessId}/costs`)
      const json = await res.json() as any
      if (json.success) {
        setCosts(json.data.costs ?? json.data)
        setSummary(json.data.summary ?? null)
      } else {
        setError(json.error?.message || 'Gagal memuat biaya')
      }
    } catch {
      setError('Gagal memuat biaya')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { fetchCosts() }, [businessId])

  const createCost = async (data: Omit<BusinessCost, 'id' | 'businessId' | 'createdAt' | 'updatedAt'>) => {
    const res = await fetch(`/api/businesses/${businessId}/costs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    const json = await res.json() as any
    if (json.success) await fetchCosts()
    return json
  }

  const updateCost = async (costId: string, data: Partial<BusinessCost>) => {
    const res = await fetch(`/api/businesses/${businessId}/costs/${costId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    const json = await res.json() as any
    if (json.success) await fetchCosts()
    return json
  }

  const deleteCost = async (costId: string) => {
    const res = await fetch(`/api/businesses/${businessId}/costs/${costId}`, { method: 'DELETE' })
    const json = await res.json() as any
    if (json.success) await fetchCosts()
    return json
  }

  return { costs, summary, isLoading, error, createCost, updateCost, deleteCost, refetch: fetchCosts }
}
